import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import Notification from '@this/src/notification';
import TripAiMessagesContainer from './components/TripAiMessagesContainer';
import { DebugPanel, useDebugLog } from './debug';
import { useTrip } from './hooks/useTrip';
import { useReservationInfo } from './hooks/useReservationInfo';
import {
  sendAiMessage,
  fetchProposalBundles,
  rejectProposalBundle,
  selectProposalItem,
  completeProposalSelection,
  isForeignFlightTravelerFields,
  saveReservationInfo
} from './api';
import { PROPOSAL_BUNDLE_STATE, type ProposalBundle, type ProposalBundleState } from './types/proposalBundles';
import type { ApplyProposalItemSelect } from './types/proposalHandlers';
import type { ChatMessage, FeedbackType } from './types/messages';
import type { FormValues } from './types/reservationInfoForm';
import type { UserInfo } from './types/user';
import type { ProposalNotification } from './hooks/useAiMessages';
import { getTripStatusLabel } from './types/tripStatus';

const applySelectToBundles = (
  bundles: ProposalBundle[],
  bundleId: number,
  itemId: number,
  applyUpdate: ApplyProposalItemSelect
): ProposalBundle[] | null => {
  const current = bundles.find(bundle => bundle.id === bundleId);
  if (!current) {
    return null;
  }

  const targetItem = current.items.find(item => item.id === itemId);
  if (!targetItem || targetItem.selected) {
    return null;
  }

  const nextBundle = applyUpdate(current);
  return bundles.map(bundle => (bundle.id !== bundleId ? bundle : nextBundle));
};

interface Props {
  tripId: string;
  user: UserInfo;
  initialMessage: string | undefined;
  initialMessages: ChatMessage[];
}

const TripAiMessagesPage = ({ tripId, user, initialMessage, initialMessages }: Props) => {
  const location = useLocation();
  const history = useHistory();
  const { trip, pusherInfo, loading, error, refetch } = useTrip(tripId);
  const { reservationInfo, refetch: refetchReservationInfo } = useReservationInfo(tripId);

  const debugLog = useDebugLog();
  const debugLogRef = useRef(debugLog);
  debugLogRef.current = debugLog;

  const [proposalNotification, setProposalNotification] = useState<ProposalNotification | null>(null);
  const [proposalBundles, setProposalBundlesState] = useState<ProposalBundle[]>([]);
  const proposalBundlesRef = useRef(proposalBundles);
  const [isSubmittingReservationInfo, setIsSubmittingReservationInfo] = useState(false);
  const [selectionRequests, setSelectionRequests] = useState(0);
  const [isReservationInfoFormEditable, setIsReservationInfoFormEditable] = useState(false);

  const setProposalBundles = useCallback((bundles: ProposalBundle[]) => {
    proposalBundlesRef.current = bundles;
    setProposalBundlesState(bundles);
  }, []);

  // for debug
  useEffect(() => {
    if (!trip) return;
    const tripUser = trip.user;
    const org = tripUser?.organization;

    debugLogRef.current.setContextInfo({
      trip: {
        id: trip.id,
        finalDestination: trip.final_destination,
        purpose: trip.purpose,
        status: trip.status,
        draft: trip.draft
      },
      user: tripUser
        ? {
            id: tripUser.id,
            name: tripUser.name,
            email: tripUser.email
          }
        : undefined,
      organization: org
        ? {
            id: org.id,
            name: org.name,
            aiReservationEnabled: org.ai_reservation_enabled,
            workflowStyle: org.workflow_style
          }
        : undefined
    });
  }, [trip]);

  const handleProposalReceived = useCallback((notification: ProposalNotification) => {
    setProposalNotification(notification);
  }, []);

  const handleSelectItem = useCallback(
    async (bundleId: number, itemId: number, applyUpdate: ApplyProposalItemSelect) => {
      const previousBundles = proposalBundlesRef.current;
      const nextBundles = applySelectToBundles(previousBundles, bundleId, itemId, applyUpdate);
      if (!nextBundles) {
        return;
      }

      setProposalBundles(nextBundles);

      const startTime = Date.now();
      try {
        setSelectionRequests(prev => prev + 1);
        const result = await selectProposalItem(tripId, bundleId, itemId);
        debugLogRef.current.logApi('POST', `/trips/${tripId}/proposal_bundles/${bundleId}/select`, {
          status: 200,
          duration: Date.now() - startTime,
          response: { bundleId, itemId, ...result }
        });
      } catch (e) {
        debugLogRef.current.logError('Failed to select proposal item', e);
        if (proposalBundlesRef.current === nextBundles) {
          setProposalBundles(previousBundles);
        }
      } finally {
        setSelectionRequests(prev => (prev > 0 ? prev - 1 : 0));
      }
    },
    [tripId, setProposalBundles]
  );

  const handleCloseBundle = useCallback(
    async (bundleId: number, updatedBundle: ProposalBundle) => {
      const previousBundles = proposalBundlesRef.current;
      const targetBundle = previousBundles.find(bundle => bundle.id === bundleId);
      if (!targetBundle || targetBundle.state === 'rejected') {
        return;
      }

      const nextBundles = previousBundles.map(bundle => (bundle.id !== bundleId ? bundle : updatedBundle));
      setProposalBundles(nextBundles);

      const startTime = Date.now();
      try {
        setSelectionRequests(prev => prev + 1);
        const result = await rejectProposalBundle(tripId, bundleId);
        debugLogRef.current.logApi('POST', `/trips/${tripId}/proposal_bundles/${bundleId}/reject`, {
          status: 200,
          duration: Date.now() - startTime,
          response: { bundleId, ...result }
        });
      } catch (e) {
        debugLogRef.current.logError('Failed to reject proposal bundle', e);
        if (proposalBundlesRef.current === nextBundles) {
          setProposalBundles(previousBundles);
        }
      } finally {
        setSelectionRequests(prev => (prev > 0 ? prev - 1 : 0));
      }
    },
    [tripId, setProposalBundles]
  );

  const handleBundleFeedbackUpdate = useCallback(
    (bundleId: number, feedback: FeedbackType, commentSubmitted: boolean) => {
      const nextBundles = proposalBundlesRef.current.map(bundle =>
        bundle.id === bundleId ? { ...bundle, feedback, feedbackCommentSubmitted: commentSubmitted } : bundle
      );
      setProposalBundles(nextBundles);
    },
    [setProposalBundles]
  );

  useEffect(() => {
    if (!proposalNotification) {
      return undefined;
    }

    let mounted = true;
    const loadBundles = async () => {
      const startTime = Date.now();
      try {
        const bundles = await fetchProposalBundles(tripId);
        if (!mounted) return;
        debugLogRef.current.logApi('GET', `/trips/${tripId}/proposal_bundles`, {
          status: 200,
          duration: Date.now() - startTime,
          response: { bundleCount: bundles.length, bundles }
        });
        setProposalBundles(bundles);
      } catch (e) {
        if (!mounted) return;
        debugLogRef.current.logError('Failed to fetch proposal bundles', e);
      }
    };

    loadBundles();
    return () => {
      mounted = false;
    };
  }, [proposalNotification, tripId]);

  const handleSubmitReservationInfo = useCallback(
    async (values: FormValues): Promise<boolean> => {
      setIsSubmittingReservationInfo(true);
      const startTime = Date.now();
      try {
        const result = await saveReservationInfo(tripId, {
          finalDestination: values.finalDestination,
          purpose: values.purpose,
          internalNumber: values.internalNumber,
          chargingDepartmentId: values.chargingDepartmentId,
          projectId: values.projectId,
          organizationAddressId: values.shinkansen?.organizationAddressId,
          shinkansenAddressType: values.shinkansen?.shinkansenAddressType,
          postcode: values.shinkansen?.postcode,
          shinkansenAddress: values.shinkansen?.shinkansenAddress,
          shinkansenAddressee: values.shinkansen?.shinkansenAddressee,
          preferredTicketingMethod: values.shinkansen?.preferredTicketingMethod || undefined,
          traveler: values.traveler,
          foreignFlightTraveler: isForeignFlightTravelerFields(reservationInfo?.travelerFields ?? null),
          pickupStations: values.pickupStations
        });
        debugLogRef.current.logApi('POST', `/trips/${tripId}/reservation_info`, {
          status: result.success ? 200 : 400,
          duration: Date.now() - startTime,
          response: result
        });
        if (result.success) {
          await refetch();
          await refetchReservationInfo();
          return true;
        }
        Notification.error(result.error || '保存に失敗しました');
        return false;
      } catch (e) {
        debugLogRef.current.logError('Failed to save reservation info', e);
        Notification.error('保存に失敗しました。時間をおいてもう一度お試しください。');
        return false;
      } finally {
        setIsSubmittingReservationInfo(false);
      }
    },
    [tripId, refetch, refetchReservationInfo, reservationInfo?.travelerFields]
  );

  useEffect(() => {
    if (proposalBundles.length === 0) {
      return;
    }
    if (selectionRequests > 0) {
      return;
    }

    const terminalProposalBundleStates: readonly ProposalBundleState[] = [
      PROPOSAL_BUNDLE_STATE.SELECTED,
      PROPOSAL_BUNDLE_STATE.REJECTED
    ];
    const allDecided = proposalBundles.every(bundle => terminalProposalBundleStates.includes(bundle.state));

    if (allDecided) {
      const complete = async () => {
        const startTime = Date.now();
        try {
          const result = await completeProposalSelection(tripId);
          debugLogRef.current.logApi('POST', `/trips/${tripId}/proposal_bundles/complete`, {
            status: 200,
            duration: Date.now() - startTime,
            response: { ...result, bundleStates: proposalBundles.map(b => ({ id: b.id, state: b.state })) }
          });
        } catch (e) {
          debugLogRef.current.logError('Failed to complete proposal selection', e);
        }
      };
      complete();
    }
  }, [proposalBundles, tripId, selectionRequests]);

  const hasSentInitialRef = useRef(false);
  const prevTripIdRef = useRef(tripId);
  const prevInitialMessageRef = useRef(initialMessage);

  useEffect(() => {
    if (prevTripIdRef.current !== tripId || prevInitialMessageRef.current !== initialMessage) {
      hasSentInitialRef.current = false;
      prevTripIdRef.current = tripId;
      prevInitialMessageRef.current = initialMessage;
    }
  }, [tripId, initialMessage]);

  useEffect(() => {
    if (!initialMessage || hasSentInitialRef.current) return;
    if (!trip || !pusherInfo) return;

    hasSentInitialRef.current = true;
    const sendInitial = async () => {
      try {
        await sendAiMessage(tripId, initialMessage, []);
        history.replace(location.pathname, {});
      } catch {
        Notification.error('送信に失敗しました。時間をおいてもう一度お試しください。');
      }
    };
    sendInitial();
  }, [initialMessage, tripId, trip, pusherInfo, history, location.pathname]);

  if (error) {
    return (
      <>
        <div style={{ padding: '24px' }}>{error}</div>
        <DebugPanel showToggleIcon={false} proposalBundles={proposalBundles} reservationInfo={reservationInfo} />
      </>
    );
  }

  const allowPartial = Boolean(initialMessage);
  if ((loading || !trip || !pusherInfo) && !allowPartial) {
    return <Loading />;
  }

  return (
    <>
      <TripAiMessagesContainer
        tripId={tripId}
        trip={trip || null}
        user={user}
        pusherKey={pusherInfo?.key || ''}
        pusherChannelPrefix={pusherInfo?.channelPrefix || ''}
        headerTitle={`旅程番号：${tripId}`}
        subtitle={getTripStatusLabel(trip?.status, trip?.draft)}
        enableMessaging={!loading && !!trip && !!pusherInfo}
        initialMessages={initialMessages}
        onProposalReceived={handleProposalReceived}
        onReservationFormReceived={refetchReservationInfo}
        proposalBundles={proposalBundles}
        onSelectItem={handleSelectItem}
        onCloseItem={handleCloseBundle}
        onBundleFeedbackUpdate={handleBundleFeedbackUpdate}
        reservationInfo={reservationInfo}
        onSubmitReservationInfo={handleSubmitReservationInfo}
        isSubmittingReservationInfo={isSubmittingReservationInfo}
        onArrangementRequestSuccess={refetch}
        onReservationInfoFormEditableChange={setIsReservationInfoFormEditable}
        isReservationInfoFormEditable={isReservationInfoFormEditable}
        onTripUpdated={async () => {
          await refetch();
          await refetchReservationInfo();
        }}
      />
      <DebugPanel showToggleIcon={false} proposalBundles={proposalBundles} reservationInfo={reservationInfo} />
    </>
  );
};

export default TripAiMessagesPage;
